import * as React from 'react';
import DOMPurify from 'dompurify';
import { templateReplace, percentileBase, percentileLabel } from '../helpers';

// Admin-authored template strings may contain basic HTML (e.g. <b>, <br>).
// Row data values are interpolated in via {{field}} placeholders and must be
// escaped first — otherwise a value like `FP4 <script>…</script>` would
// execute when the tooltip renders. We escape values, then still run the
// assembled HTML through DOMPurify as defense-in-depth.
const HTML_ESCAPE = {'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'};

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, ch => HTML_ESCAPE[ch]);
}

function fillTemplate(template, entry) {
  return template.replace(/{{\s*([^}]+)\s*}}/g, (_, key) => escapeHtml(entry[key.trim()]));
}

function isPopulated(value) {
  return value != null && value !== '';
}

// One tooltip line for a config section, or null when it shouldn't render.
// Config-driven and generic — field names/labels come from the config, never
// hardcoded — so shape-adaptive tooltips (fixed/dynamic, agg/disagg) are just
// data on the chart config, reusable across charts.
//   - { text, when? } — a template line; skipped when `when` field is empty.
//   - { dims, labels?, prefix?, join? } — renders only the populated fields from
//     `dims` as "LABEL=value" (skips null dims, e.g. TP=4 · EP=2).
function renderSection(section, entry) {
  if (section.when && !isPopulated(entry[section.when])) return null;
  if (Array.isArray(section.dims)) {
    const parts = section.dims
      .filter(field => isPopulated(entry[field]))
      .map(field => `${section.labels?.[field] || field}=${escapeHtml(entry[field])}`);
    if (parts.length === 0) return null;
    return `${section.prefix || ''}${parts.join(section.join || ' · ')}`;
  }
  return fillTemplate(section.text || '', entry);
}

function tooltipLines(tooltip, entry) {
  if (Array.isArray(tooltip.advanced)) {
    return tooltip.advanced.map(section => renderSection(section, entry)).filter(isPopulated);
  }
  // Backward-compatible single-template form.
  return String(tooltip.text || '').split('\n').map(part => fillTemplate(part, entry)).filter(line => line !== '');
}

export default function ChartTooltip(props) {
  const {chartConfig, hoveredDot} = props;

  if (!hoveredDot || !hoveredDot.entry) return null;

  const {title} = chartConfig.tooltip;
  const {entry, stroke} = hoveredDot;

  // Surface the *selected* interactivity percentile (§1.5): expose the current
  // X-axis value and, when X is a percentile field, its label as synthetic
  // `x_value` / `x_percentile` fields the config template can reference. This
  // stays generic — the tooltip config drives what's shown, we only compute the
  // dynamic X the axis switcher chose.
  // A band edge carries its own field via hoveredDot.xField (e.g. the p90 edge),
  // so each edge's tooltip shows its own value/percentile. Non-band dots have no
  // xField and fall back to the shared configured X field.
  const xField = hoveredDot.xField || chartConfig.axes?.x?.field;
  const xBase = percentileBase(xField);
  const xToken = xBase ? xField.slice(0, xField.length - xBase.length - 1) : null;
  const augmented = {
    ...entry,
    ...(xField ? { x_value: entry[xField] } : {}),
    ...(xToken ? { x_percentile: percentileLabel(xToken) } : {}),
  };

  const entryText = DOMPurify.sanitize(
    tooltipLines(chartConfig.tooltip, augmented).map(line => `<div>${line}</div>`).join('')
  );
  return (
    <div className="nv-chart-tooltip">
      <div className='nv-chart-tooltip__group'>
        <div className='nv-chart-tooltip__label'>{templateReplace(title, augmented)}</div>
        <div className='nv-chart-tooltip__content'>
          <div className='nv-chart-tooltip__marker' style={{backgroundColor: stroke}}></div>
          <div className='nv-chart-tooltip__text' dangerouslySetInnerHTML={{__html: entryText}}></div>
        </div>
      </div>
    </div>
  );
}
